import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LuSearch } from "react-icons/lu";
import "./search.css";

const Search = ({ isLoggedIn, loginUser, onLogout }) => {
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchUsers = useCallback(async (q) => {
    setLoading(true);
    try {
      const res = await axios.get("/api/v1/user/search", { params: { q, limit: 30 } });
      setUsers(res.data.users || []);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        onLogout();
        navigate("/login");
      }
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [onLogout, navigate]);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }
  }, [isLoggedIn, navigate]);

  // Debounced search — only query once there are 2+ characters
  useEffect(() => {
    if (!isLoggedIn) return;
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setUsers([]);
      setLoading(false);
      return;
    }
    const t = setTimeout(() => fetchUsers(trimmed), 250);
    return () => clearTimeout(t);
  }, [query, fetchUsers, isLoggedIn]);

  return (
    <div className="sr-search">
      <div className="sr-search__top">
        <div className="sr-search__top-content">
          <h1 className="sr-search__title">Find People</h1>
          <p className="sr-search__subtitle">Search drivers and passengers</p>
        </div>
      </div>

      <div className="sr-search__body">
        <div className="sr-search__bar">
          <LuSearch size={18} className="sr-search__bar-icon" />
          <input
            className="sr-search__bar-input"
            type="text"
            placeholder="Search by name or email..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>

        <div className="sr-search__list">
          {loading && users.length === 0 && (
            [1, 2, 3, 4, 5].map((i) => (
              <div className="sr-search__card sr-search__card--skeleton" key={i}>
                <div className="sr-search__skeleton sr-search__skeleton--avatar" />
                <div className="sr-search__skeleton-body">
                  <div className="sr-search__skeleton sr-search__skeleton--name" />
                  <div className="sr-search__skeleton sr-search__skeleton--stats" />
                </div>
              </div>
            ))
          )}

          {!loading && users.length === 0 && (
            <div className="sr-search__empty">
              <p>{query.trim().length < 2 ? "Type at least 2 characters to search." : "No users found."}</p>
            </div>
          )}

          {users.map((u) => (
            <button key={u._id} className="sr-search__card" onClick={() => navigate(`/profile/${u._id}`)}>
              <img src={u.avatar} alt="" className="sr-search__avatar" />
              <div className="sr-search__body-col">
                <div className="sr-search__name-row">
                  <span className="sr-search__name">{u.userName}</span>
                  {u.isApproved && <span className="sr-search__badge sr-search__badge--trusted">Verified</span>}
                </div>
                <div className="sr-search__stats">
                  <span><strong>{u.ridesOffered ?? 0}</strong> offered</span>
                  <span>·</span>
                  <span><strong>{u.ridesTaken ?? 0}</strong> taken</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Search;
